import AbstractService from '~/composables/analytics/services/AbstractService'
import getFacebookPixelIdsByLanguageId from '~/utils/config/getFacebookPixelIdsByLanguageId'
import { useLanguageStore } from '~/store/language'
import generateRandomUUID from '~/utils/generateRandomUUID'
import type ProductViewData from '~/types/analytics/marketplace/ProductViewData'
import type ProductAddedToCartData from '~/types/analytics/marketplace/ProductAddedToCartData'
import type BeginCheckoutData from '~/types/analytics/marketplace/BeginCheckoutData'
import type AddPaymentInfoData from '~/types/analytics/marketplace/AddPaymentInfoData'
import type PayButtonClickedData from '~/types/analytics/marketplace/PayButtonClickedData'
import type PaymentSuccessData from '~/types/analytics/checkout/PaymentSuccessData'
import type PaymentDeclinedData from '~/types/analytics/checkout/PaymentDeclinedData'
import type QuotePaymentPageViewData from '~/types/analytics/quotes/checkout/QuotePaymentPageViewData'
import type QuotePaymentPageAddedDeliveryInfoData from '~/types/analytics/quotes/checkout/QuotePaymentPageAddedDeliveryInfoData'
import type QuotePaymentPageAddedPaymentInfoData from '~/types/analytics/quotes/checkout/QuotePaymentPageAddedPaymentInfoData'
import type QuotePaymentPagePayButtonClickData from '~/types/analytics/quotes/checkout/QuotePaymentPagePayButtonClickData'
enum EventName {
  QuoteRequestSuccess = 'QuoteRequestSuccess',
  PageView = 'PageView',
  ButtPay = 'ButtPay',
  MarketplaceViewContent = 'MarketplaceViewContent',
  MarketplaceAddToCart = 'MarketplaceAddToCart',
  MarketplaceInitiateCheckout = 'MarketplaceInitiateCheckout',
  MarketplaceAddPaymentInfo = 'MarketplaceAddPaymentInfo',
  MarketplacePurchase = 'MarketplacePurchase',
  MarketplacePaymentSuccess = 'marketplace_payment_success',
  QuotePaymentSuccess = 'quote_payment_success',
  MarketplacePaymentDeclined = 'marketplace_payment_declined',
  QuotePaymentDeclined = 'quote_payment_declined',

  QuotePaymentPageView = 'QuotePaymentPageView',
  QuotePaymentPageAddedDeliveryInfo = 'QuotePaymentPageAddedDeliveryInfo',
  QuotePaymentPageAddedPaymentInfo = 'QuotePaymentPageAddedPaymentInfo',
  QuotePaymentPagePayButtonClick = 'QuotePaymentPagePayButtonClick',
}

enum EventType {
  track = 'track',
  trackCustom = 'trackCustom',
}

export default class extends AbstractService {
  private readonly DEFAULT_CURRENCY_CODE = 'USD'

  private trackFacebookEvent(eventType: EventType, eventName: string, data: Record<string, any>) {
    const eventId = generateRandomUUID()
    const facebookPixelInstance = this.getFbqInstance()

    if (facebookPixelInstance) {
      facebookPixelInstance(eventType, eventName, data, { eventID: eventId })
    }

    // Todo: make async request to track on server side
  }

  private getFbqInstance(): Function | null {
    if (typeof window === 'undefined') {
      return null
    }

    if (Object.prototype.hasOwnProperty.call(window, 'fbq')) {
      return (window as any).fbq
    }

    return null
  }

  public trackQuoteSubmit(quoteId: number): void {
    this.trackFacebookEvent(EventType.trackCustom, EventName.QuoteRequestSuccess, {
      quoteId,
    })
  }

  public trackPageLoad(): void {
    const facebookPixelInstance = this.getFbqInstance()

    if (facebookPixelInstance) {
      const languageId = useLanguageStore().getLanguageId

      getFacebookPixelIdsByLanguageId(languageId).forEach((predicate) => facebookPixelInstance('init', predicate))
    }
  }

  public trackPageView(_data: Record<string, any>): void {
    this.trackFacebookEvent(EventType.track, EventName.PageView, {})
  }

  public trackQuotePaid(quoteId: number, valueInUSD: number): void {
    this.trackFacebookEvent(EventType.trackCustom, EventName.ButtPay, {
      quote_id: quoteId,
      currency: this.DEFAULT_CURRENCY_CODE,
      value: valueInUSD,
    })
  }

  trackMarketplaceProductView(data: ProductViewData) {
    this.trackFacebookEvent(EventType.trackCustom, EventName.MarketplaceViewContent, {
      content_ids: data.sku,
      content_type: 'product',
      currency: this.DEFAULT_CURRENCY_CODE,
      value: data.price,
    })
  }

  trackMarketplaceProductAddedToCart(data: ProductAddedToCartData) {
    this.trackFacebookEvent(EventType.trackCustom, EventName.MarketplaceAddToCart, {
      currency: this.DEFAULT_CURRENCY_CODE,
      value: data.price,
      content_name: data.name,
      content_type: 'product',
      content_ids: data.sku,
      num_items: data.quantity,
    })
  }

  trackBeginCheckout(data: BeginCheckoutData) {
    this.trackFacebookEvent(EventType.trackCustom, EventName.MarketplaceInitiateCheckout, {
      currency: this.DEFAULT_CURRENCY_CODE,
      num_items: data.items
        .map((item) => item.quantity)
        .reduce((previousValue, currentValue) => previousValue + currentValue, 0),
      value: data.items
        .map((item) => item.price * item.quantity)
        .reduce((previousValue, currentValue) => previousValue + currentValue, 0.0)
        .toFixed(2),
    })
  }

  trackMarketplaceAddPaymentInfo(data: AddPaymentInfoData) {
    this.trackFacebookEvent(EventType.trackCustom, EventName.MarketplaceAddPaymentInfo, {
      currency: this.DEFAULT_CURRENCY_CODE,
      num_items: data.items
        .map((item) => item.quantity)
        .reduce((previousValue, currentValue) => previousValue + currentValue, 0),
      value: data.value,
      content_type: 'product',
      contents: data.items.map((predicate) => {
        return {
          id: predicate.sku,
          quantity: predicate.quantity,
        }
      }),
      paymentType: data.paymentType,
    })
  }

  trackMarketplacePayButtonClick(data: PayButtonClickedData) {
    this.trackFacebookEvent(EventType.trackCustom, EventName.MarketplacePurchase, {
      currency: this.DEFAULT_CURRENCY_CODE,
      num_items: data.items
        .map((item) => item.quantity)
        .reduce((previousValue, currentValue) => previousValue + currentValue, 0),
      value: data.value,
      content_type: 'product',
      contents: data.items.map((predicate) => {
        return {
          id: predicate.sku,
          quantity: predicate.quantity,
        }
      }),
    })
  }

  trackPaymentSuccessPage(data: PaymentSuccessData) {
    if (data.marketplace) {
      return this.trackFacebookEvent(EventType.trackCustom, EventName.MarketplacePaymentSuccess, {
        currency: this.DEFAULT_CURRENCY_CODE,
        value: data.value,
        quote_id: data.quoteId,
        content_type: 'product',
        contents: data.items.map((predicate) => {
          return {
            id: predicate.sku,
            quantity: predicate.quantity,
          }
        }),
      })
    }

    return this.trackFacebookEvent(EventType.trackCustom, EventName.QuotePaymentSuccess, {
      currency: this.DEFAULT_CURRENCY_CODE,
      value: data.value,
      quote_id: data.quoteId,
    })
  }

  trackPaymentDeclinedPage(data: PaymentDeclinedData) {
    if (data.marketplace) {
      return this.trackFacebookEvent(EventType.trackCustom, EventName.MarketplacePaymentDeclined, {
        quote_id: data.quoteId,
      })
    }

    return this.trackFacebookEvent(EventType.trackCustom, EventName.QuotePaymentDeclined, {
      quote_id: data.quoteId,
    })
  }

  trackQuotePaymentPageView(data: QuotePaymentPageViewData) {
    return this.trackFacebookEvent(EventType.trackCustom, EventName.QuotePaymentPageView, {
      quote_id: data.quoteId,
      currency: this.DEFAULT_CURRENCY_CODE,
      value: data.value,
    })
  }

  trackQuotePaymentPageAddedDeliveryInfo(data: QuotePaymentPageAddedDeliveryInfoData) {
    return this.trackFacebookEvent(EventType.trackCustom, EventName.QuotePaymentPageAddedDeliveryInfo, {
      quote_id: data.quoteId,
      currency: this.DEFAULT_CURRENCY_CODE,
      value: data.value,
      shipping: data.shipping,
      deliveryFee: data.deliveryFee,
    })
  }

  trackQuotePaymentPageAddedPaymentInfo(data: QuotePaymentPageAddedPaymentInfoData) {
    return this.trackFacebookEvent(EventType.trackCustom, EventName.QuotePaymentPageAddedPaymentInfo, {
      quote_id: data.quoteId,
      currency: this.DEFAULT_CURRENCY_CODE,
      value: data.value,
      shipping: data.shipping,
      deliveryFee: data.deliveryFee,
      paymentType: data.paymentType,
    })
  }

  trackQuotePaymentPagePayButtonClick(data: QuotePaymentPagePayButtonClickData) {
    return this.trackFacebookEvent(EventType.trackCustom, EventName.QuotePaymentPagePayButtonClick, {
      quote_id: data.quoteId,
      currency: this.DEFAULT_CURRENCY_CODE,
      value: data.value,
      shipping: data.shipping,
      deliveryFee: data.deliveryFee,
      paymentType: data.paymentType,
    })
  }
}
